import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { AccessTime as AccessTimeIcon, ChatBubbleOutline as ChatBubbleIcon } from '@mui/icons-material';
import { MESSAGE_DISPLAY_LIMIT } from '../../types/common';
import { Patient, } from '../../types/patient';
import { NURSE_TYPE_THEME, ROLE_COLORS as NURSE_ROLE_COLORS, ROLE_TYPES as NURSE_ROLE_TYPES } from '../../types/nurse';
import { TALK_STATUS_TYPES } from '../../types/talk';
import { ChatMessageDownloadButton, ExternalLinkButton, GenderAgeDisplay, NameNicknameDisplay } from '../common';
import { formatDateWithTime, getHumanReadableTimeAgo } from '../../utils/dateUtils';
import { getExternalTalkRoomUrl } from '../../utils/urlUtils';

interface RelatedNursesSectionProps {
  patient: Patient;
  loadingError?: string | null;
}

/**
 * 患者と対話したことのある看護師一覧を表示するセクション
 */
const RelatedNursesSection: React.FC<RelatedNursesSectionProps> = ({
  patient,
  loadingError
}) => {
  const navigate = useNavigate();
  // 対話歴のある看護師がいるか確認
  if (!patient.related_nurses || patient.related_nurses.length === 0) {
    // エラーメッセージまたは該当なしメッセージ
    return (
      <Card
        sx={{
          mb: 4,
          border: `1px solid ${NURSE_TYPE_THEME.COMMON.BORDER}40`,
          boxShadow: `0 1px 4px ${NURSE_TYPE_THEME.COMMON.BACKGROUND}30`
        }}>
        <CardContent>
          <Grid container>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>対話したことがある看護師</Typography>
              <Divider sx={{ mb: 2 }}/>

              <Box sx={{ p: 2 }}>
                <Typography
                  variant="body1"
                  sx={{ color: loadingError ? 'error.main' : 'text.disabled', fontStyle: 'italic' }}
                >
                  {loadingError || '対話歴のある看護師はいません'}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card
      sx={{
        mb: 4,
        border: `1px solid ${NURSE_TYPE_THEME.COMMON.BORDER}40`,
        boxShadow: `0 1px 4px ${NURSE_TYPE_THEME.COMMON.BACKGROUND}30`
      }}>
      <CardContent>
        <Grid container>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6">対話したことがある看護師</Typography>
              {patient.related_nurses.length >= 5 && (
                <Chip
                  label={`${patient.related_nurses.length}人`}
                  size="small"
                  variant="outlined"
                  sx={{
                    ml: 1,
                    fontWeight: 'bold',
                    color: NURSE_TYPE_THEME.NORMAL.DARK,
                    borderColor: NURSE_TYPE_THEME.NORMAL.DARK,
                    borderWidth: 1.5
                  }}
                />
              )}
            </Box>
            <Divider sx={{ mb: 2 }}/>

            <Box
              sx={{
                mt: 2,
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '20px',
                  background: `linear-gradient(to top, ${NURSE_TYPE_THEME.NORMAL.BACKGROUND}CC, ${NURSE_TYPE_THEME.NORMAL.BACKGROUND}00)`,
                  pointerEvents: 'none',
                  zIndex: 1,
                  display: 'block'
                }
              }}
            >
              <TableContainer
                component={Paper}
                sx={{
                  borderRadius: 1,
                  maxHeight: '500px', // 最大高さを指定
                  overflow: 'auto', // スクロール可能に
                  '&::-webkit-scrollbar': {
                    width: '8px',
                    backgroundColor: `${NURSE_TYPE_THEME.NORMAL.BACKGROUND}`,
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: `${NURSE_TYPE_THEME.NORMAL.BORDER}`,
                    borderRadius: '4px',
                    '&:hover': {
                      backgroundColor: NURSE_TYPE_THEME.NORMAL.DARK
                    }
                  }
                }}
              >
                <Table
                  stickyHeader
                  aria-label="対話歴のある看護師一覧"
                  sx={{
                    "& .MuiTableHead-root": {
                      position: "sticky",
                      top: 0,
                      zIndex: 2
                    }
                  }}>
                  <TableHead
                    sx={{
                      "& .MuiTableCell-root": {
                        backgroundColor: `${NURSE_TYPE_THEME.NORMAL.BACKGROUND} !important`,
                        borderBottom: `2px solid ${NURSE_TYPE_THEME.NORMAL.BORDER}`
                      }
                    }}>
                    <TableRow>
                      <TableCell width="7%" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                        ロール
                      </TableCell>
                      <TableCell width="25%" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                        名前 / ニックネーム
                      </TableCell>
                      <TableCell width="10%" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                        性別 (年齢)
                      </TableCell>
                      <TableCell width="15%" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                        受付状態
                      </TableCell>
                      <TableCell width="30%" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                        最終メッセージ / 日時
                      </TableCell>
                      <TableCell width="13%" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                        対話状態 / チャット
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {patient.related_nurses
                    .slice() // 元の配列を変更しないようにコピー
                    .sort((a, b) => {
                      // 対話ルーム作成日時がない場合は後ろに配置
                      if (!a.latest_talk_room_created_at) return 1;
                      if (!b.latest_talk_room_created_at) return -1;
                      // 降順（新しい順）に並べる
                      return new Date(b.latest_talk_room_created_at).getTime() - new Date(a.latest_talk_room_created_at).getTime();
                    })
                    .map((nurse) => (
                      <TableRow
                        key={nurse.id}
                        hover
                        component={Link}
                        to={`/talk_rooms/summary?nurse_id=${nurse.id}&patient_id=${patient.id}`}
                        sx={{
                          textDecoration: 'none',
                          color: 'inherit',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          },
                          ...(nurse.resigned_at ? {
                            backgroundColor: '#fcf5f5',
                            opacity: 0.8,
                            '& td': { color: '#777' },
                          } : {})
                        }}
                      >
                        {/* ロール */}
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              color: NURSE_ROLE_COLORS[nurse.role_type as keyof typeof NURSE_ROLE_COLORS] || '#757575',
                              fontSize: '0.75rem',
                              borderLeft: `3px solid ${NURSE_ROLE_COLORS[nurse.role_type as keyof typeof NURSE_ROLE_COLORS] || '#757575'}`,
                              pl: 1,
                              py: 0.3,
                              opacity: 0.85,
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {NURSE_ROLE_TYPES[nurse.role_type as keyof typeof NURSE_ROLE_TYPES] || nurse.role_type}
                          </Typography>
                        </TableCell>

                        {/* 看護師名とニックネーム */}
                        <TableCell>
                          <NameNicknameDisplay
                            name={nurse.name}
                            nickname={nurse.nickname}
                            nameFontWeight="bold"
                            isTestAccount={(nurse as any)?.is_test_account || false}
                          />
                        </TableCell>

                        {/* 性別（年齢） */}
                        <TableCell>
                          <GenderAgeDisplay
                            genderType={nurse.gender_type || ''}
                            birthday={nurse.birthday || ''}
                            fontSize="0.9rem"
                          />
                        </TableCell>

                        {/* 受付状態 */}
                        <TableCell>
                          {nurse.can_assign !== undefined && (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography
                                component="span"
                                sx={{
                                  color: nurse.can_assign ? '#2e7d32' : '#c62828',
                                  fontWeight: 'bold',
                                  fontSize: '1.2rem',
                                  mr: 0.5,
                                  ...(nurse.resigned_at ? { opacity: 0.6 } : {})
                                }}
                              >
                                {nurse.can_assign ? '○' : '×'}
                              </Typography>
                              <Typography
                                component="span"
                                sx={{
                                  color: nurse.can_assign ? '#2e7d32' : '#c62828',
                                  fontSize: '0.9rem',
                                  ...(nurse.resigned_at ? { opacity: 0.6 } : {})
                                }}
                              >
                                {nurse.can_assign ? '受付中' : '停止中'}
                              </Typography>
                            </Box>
                          )}
                        </TableCell>

                        {/* 最終メッセージ内容と送信日時 */}
                        <TableCell>
                          {nurse.latest_talk_last_chat_message ? (
                            nurse.latest_talk_last_chat_message.length > MESSAGE_DISPLAY_LIMIT ? (
                              // 制限文字数を超える場合はTooltipを表示
                              <Tooltip
                                title={nurse.latest_talk_last_chat_message}
                                arrow
                                placement="top"
                                enterDelay={500}
                                sx={{ maxWidth: 500 }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: '0.85rem',
                                    color: 'text.primary',
                                    cursor: 'help'
                                  }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    {`${nurse.latest_talk_last_chat_message.substring(0, MESSAGE_DISPLAY_LIMIT)}...`}
                                    <ChatBubbleIcon sx={{ ml: 0.5, fontSize: '0.9rem', color: 'text.secondary', opacity: 0.7 }}/>
                                  </Box>
                                </Typography>
                              </Tooltip>
                            ) : (
                              // 制限文字数以内の場合はTooltipなし
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: '0.85rem',
                                  color: 'text.primary'
                                }}
                              >
                                {nurse.latest_talk_last_chat_message}
                              </Typography>
                            )
                          ) : (
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', fontSize: '0.85rem' }}>
                              メッセージなし
                            </Typography>
                          )}

                          {/* 最終チャット送信日時 */}
                          {nurse.latest_talk_last_chat_send_at && (
                            <Tooltip
                              title={`最終送信日時: ${formatDateWithTime(nurse.latest_talk_last_chat_send_at)}`}
                              arrow
                              placement="left"
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'help', mt: 0.5 }}>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                  最終送信: {getHumanReadableTimeAgo(nurse.latest_talk_last_chat_send_at)}
                                </Typography>
                                <AccessTimeIcon fontSize="small" color="action" sx={{ fontSize: '0.8rem', opacity: 0.7 }}/>
                              </Box>
                            </Tooltip>
                          )}
                        </TableCell>

                        {/* 対話ステータスとチャットリンク */}
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            {/* 対話ステータス */}
                            {nurse.latest_talk_status && (
                              <Chip
                                label={
                                  (nurse.latest_talk_status === 'DONE' && nurse.latest_talk_is_force_end)
                                    ? '強制終了'
                                    : (TALK_STATUS_TYPES[nurse.latest_talk_status as keyof typeof TALK_STATUS_TYPES] || nurse.latest_talk_status)
                                }
                                size="small"
                                sx={{
                                  fontSize: '0.75rem',
                                  alignSelf: 'flex-start',
                                  mb: 0.5,
                                  fontWeight: (nurse.latest_talk_status === 'DONE' && nurse.latest_talk_is_force_end) ? 'bold' : 'normal'
                                }}
                                color={nurse.latest_talk_status === 'DOING' ? 'success' : 'default'}
                              />
                            )}

                            {/* チャットリンク */}
                            {nurse.latest_talk_room_id && (
                              <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5 }}>
                                <ExternalLinkButton
                                  href={getExternalTalkRoomUrl(nurse.latest_talk_room_id)}
                                  label="開く"
                                  size="small"
                                  buttonType="chat"
                                  themeColor={{
                                    main: NURSE_TYPE_THEME.NORMAL.DARK,
                                    hover: NURSE_TYPE_THEME.NORMAL.BACKGROUND
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                  }}
                                />
                                <div onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }}>
                                  <ChatMessageDownloadButton
                                    roomType="TALK_ROOM"
                                    roomId={nurse.latest_talk_room_id}
                                    size="small"
                                    variant="outlined"
                                    iconOnly={true}
                                    themeColor={{
                                      main: NURSE_TYPE_THEME.NORMAL.DARK,
                                      hover: NURSE_TYPE_THEME.NORMAL.BACKGROUND
                                    }}
                                  />
                                </div>
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
export default RelatedNursesSection;
